/* ============================================================================
 * Raisy /pitchdeck — THE MASTER (v2026.10.05).
 *
 * The cherry-picked deck (@P109 D-072): mostly the co-founder's v10, with X+1's
 * structure / proceed slide / ask arithmetic and the PDF's two-differentiator
 * competition slide. Content is agreed in
 * `playano-management/raise/decks/Raisy — Pitch Deck v2026.10.05.md` — that file
 * is the source; this renders it, and a content change edits THAT file first
 * (D-075).
 *
 * Judged against `raisy/persona/persona_investor_angel.md` (Alex, DD360_Persona.100)
 * — every slide buys one of his wants W1–W10 or it is not here (D-074, D-073).
 *
 * LOCKED NUMBERS (D-068/D-069/D-070): $350K round (@P109 D-164) · $50K min ticket ·
 * 40/25/20/10/5 · Playano Education Ltd · $0/$49/$129/$279.
 *
 * PROMOTED: `/pitchdeck` renders this version — `LATEST_DECK_VERSION` is
 * `v2026.10.05` as of 2026-10-06 (D-080), which partly supersedes D-072's
 * clause that it moves only when the PO promotes it. The older versions stay
 * live at their own URLs.
 *
 * Unchanged slides are IMPORTED so versions cannot drift (the market reads the
 * generated FM-0013 mirror; the team and the legal page are shared verbatim).
 * ========================================================================== */
import { motion } from 'framer-motion';
import { useState, type ReactElement } from 'react';
import { Kicker, Shell, Stat, fadeUp, pop, stagger } from './slides';
import { ADVISORS, S9MarketV2 } from './slidesV2';
import { type Lead, postLead, readLead } from './deckLead';
// THE DEAL TERMS COME FROM THE MODEL (D-084), never from literals on the slide. The deck's
// market slide has read the generated FM-0013 mirror since D-066; these are the same
// arrangement for the round slide, so a term cannot drift between the model and the deck.
import {
  EXIT_HORIZON_YEARS,
  GTM_CUSTOMERS_THE_ROUND_BUYS,
  MIN_TICKET_SIZE,
  MIN_TICKET_EXIT_PROCEEDS,
  ROUND_IRR,
  ROUND_MOIC,
  MODEL_KPIS,
  PROCEEDS,
  PROCEEDS_ROUND,
  ROLLOUT,
  ROSTER,
  ROUND_MARKS,
} from './deckFinancials';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

/** A labelled KPI row used by the deal-on-one-page and the round slide. */
const Kpi = (p: { v: string; l: string; s?: string }) => <Stat n={p.v} l={p.l} s={p.s} />;

const KPI_CARDS: Array<[keyof typeof MODEL_KPIS, string, string, boolean]> = [
  ['arr', 'ARR at year 5', 'recurring only', true],
  ['ebitdaMargin', 'EBITDA margin', 'at year 5', true],
  ['ltvCac', 'LTV / CAC', `the round buys ${GTM_CUSTOMERS_THE_ROUND_BUYS} customers`, true],
  ['nrr', 'Net revenue retention', 'year on year', true],
  ['runRate', 'Total run-rate, Y5', 'ARR + success fees', false],
  ['grossMargin', 'Gross margin', 'blended', false],
  ['moic', 'Investor MOIC', 'on the round, at exit', false],
  ['irr', 'Investor IRR', 'per year, to exit', false],
];

/*
 * THE FOUR THE PITCH ITSELF CARRIES. `head` is a flag rather than a second list, so the mini
 * block on the ask slide and the appendix page cannot drift apart about which numbers matter —
 * the same defect as a rule with two homes, one level down.
 *
 * FOUR, NOT EIGHT, and NONE OF THEM REPEATS THE DEAL. The ask slide already states what a ticket
 * returns (MOIC, IRR, the exit proceeds on a $50K cheque); so these four answer the questions
 * that deal does not — scale, profitability, efficiency, retention. An investor's four questions,
 * one card each.
 */
export const KPI_HEADLINE = KPI_CARDS.filter((k) => k[3]);

function formatKpi(unit: 'usd' | 'pct' | 'x' | 'mo', value: number): string {
  if (unit === 'usd') return `$${(value / 1e6).toFixed(1)}M`;
  if (unit === 'pct') return `${(value * 100).toFixed(1)}%`;
  if (unit === 'x') return `${value.toFixed(2)}\u00d7`;
  return `${Math.round(value)} mo`;
}


/* ------------------------------------------------------------------ 1 cover */
export function M1Cover({ dir }: { dir: number }) {
  /* THE COVER CARRIES ITS IMAGE (the PO: "the first slide doesn't have any background pic").
   * It was passing an empty `<div className="pd-title" />` as the background, so `.pd-title` set
   * `padding: 0` and nothing else — the class was there for layout and the image was never
   * rendered. Every other deck's cover uses `/pitch/artsy/c.jpg` behind a scrim; the master was
   * the only one without it, and the asset was already in PRELOAD_MASTER. */
  return (
    <Shell
      className="pd-title"
      custom={dir}
      bg={
        <>
          <img className="pd-img" src="/pitch/artsy/c.jpg" alt="" />
          <div className="pd-scrim" />
        </>
      }
    >
      <motion.div className="pd-title-inner" variants={stagger} initial="hidden" animate="show">
        <motion.p className="pd-kick" variants={fadeUp}>
          Initializing Raisy…
        </motion.p>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Raisy<small>Every decision leaves a receipt</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          AI prepares. Humans decide. Trust is earned.
        </motion.p>
        <motion.div className="pd-chips" variants={stagger} style={{ marginTop: 26 }}>
          <motion.span className="pd-chip" variants={pop}>Raising $350K pre-seed</motion.span>
          <motion.span className="pd-chip" variants={pop}>Playano Education Ltd · Hong Kong</motion.span>
          <motion.span className="pd-chip muted" variants={pop}>v2026.10.05</motion.span>
          <motion.span className="pd-chip muted" variants={pop}>raisy.team · david@raisy.team</motion.span>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------------------- 2 the hook */
export function M2Hook({ dir }: { dir: number }) {
  /*
   * ⚠️ THIS SLIDE BROKE A RULE AND THE PO CAUGHT IT: *"@human_manifesto.md PLEASE COME ON — you are
   * talking to human."*
   *
   * It read **"Everything that can be done without you, will be."** — a head-to-head claim of the
   * machine over the person, which `human_manifesto.md` §4 rule 5 forbids in public copy verbatim:
   * *"Sell the Value, Never the Bot Over the Human… All public-facing copy must sell the value of the
   * WORKLOAD — speed, coverage, compounding, governance — never the bot as better than, or a
   * replacement for, a human. AVOID HEAD-TO-HEAD 'AI vs HUMAN' COMPARISONS in selling material;
   * position AI as the tool your people direct."*
   *
   * The manifesto's own vocabulary is the fix and it is the product promise: the machine takes the
   * JANITOR WORK, the human keeps the GENIUS WORK. So the headline is about the founder's TIME, not
   * the machine's superiority — and the split below is that sentence drawn rather than said. The
   * visual is `human_manifesto.md` §2: *"AI handles the tedious, token-burning tasks (the 'janitor
   * work' of drafting and sourcing). Humans are elevated to focus purely on high-value cognitive work
   * (the 'genius work' of strategic decision-making, emotional curation, and quality sign-off)."*
   *
   * FULL WIDTH, per his other note: *"the hook is too narrow. ITS A TERRIBLE SLIDE, just text, no
   * visualisation."* A two-band split inside the ~700px a side image leaves is not a visualisation.
   */
  const handedOver = ['Research', 'The list', 'The drafts', 'Follow-ups', 'Scheduling'];
  const kept = ['Deciding', 'Approving', 'The room', 'Being believed'];
  return (
    <Shell custom={dir} className="pd-wide-m">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The hook</Kicker>
        {/* ⚠️ THE HEADLINE WAS "You did not start a company to do the janitor's work" AND THE PO
            STOPPED IT: *"COME ON MAN. PLZ BE RESPECTFUL OF JOBS. THATS EVEN WORSE! it can be: you did
            not start a company to do fundraising."* The phrase is the manifest's own — §2 calls
            drafting and sourcing "the 'janitor work'" — but that is INTERNAL framing for what the
            machine takes on, and printing it sells our value by diminishing the people who do that
            work. §4 rule 5 forbids exactly that: *"never the bot as better than, or a replacement
            for, a human."* His replacement is better because it is SPECIFIC rather than comparative.
            **The manifest's phrase stays in the manifest; it does not go on a slide.** */}
        <motion.h1 className="pd-h1" variants={fadeUp}>
          You did not start a company to do fundraising.
          <small>
            Research, lists, drafts, follow-ups — the machine prepares all of it. The deciding, and the
            being believed, stays yours.
          </small>
        </motion.h1>
        <motion.div className="pd-split" variants={stagger}>
          <div className="pd-split-side pd-split-bot">
            <span className="pd-split-tag">The machine prepares</span>
            <div className="pd-split-items">
              {handedOver.map((h) => (
                <span className="pd-split-chip" key={h}>{h}</span>
              ))}
            </div>
            {/* ⚠️ THIS LINE SAID "The janitor work. It is not your job." — and the PO's objection
                was to the CONCEPT, not only to the headline. Fixing the headline and leaving the
                phrase in the body would have been the same rule broken one line lower, in the same
                public copy. It now sells what the machine does without naming a job at all. */}
            <span className="pd-split-note">Prepared before you ever look at it.</span>
          </div>
          <div className="pd-split-div" aria-hidden="true" />
          <div className="pd-split-side pd-split-human">
            <span className="pd-split-tag">You decide</span>
            <div className="pd-split-items">
              {kept.map((k) => (
                <span className="pd-split-chip" key={k}>{k}</span>
              ))}
            </div>
            <span className="pd-split-note">AI is the builder. The human is the architect.</span>
          </div>
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp}>
          The bottleneck is not the conversation. <b>It is everything leading up to it.</b>
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------------- 3 meet the persona */
export function M3Maya({ dir }: { dir: number }) {
  /*
   * MAYA NOW OPENS THE DECK, AND SHE CARRIES TWO NUMBERS (PO 2026-10-09).
   *
   *   *"WAY TOO MUCH TEXT on slide 3."*
   *   *"THe problem is usually quantitatively done.. here you actually have it '47 hours over 3
   *    weeks — 88% of it wasted' but not enough focus. CONFIDENCE 6/10 < WHATS THAT, you are only
   *    60% confidence and you are showing it? REMOVE THE CONFIDENCE HER ELOL."*
   *   *"make these sentences more prevalent: '47 hours over 3 weeks — 88% of it wasted', '95% of
   *    cold emails never answered · median seed round 142 days, up from 69 in 2021'"*
   *   *"AFTER the raise, we have no numbers? THEN WE CANT SHOW IT. ... if you fail, then remove it"*
   *
   * **On the confidence, he is right and the reason is worth keeping.** A confidence score is OUR
   * own internal measure of how well we know something (`PERSONA_INDEX` §10A's mark). Printed on a
   * slide it reads as a disclaimer about our own claim — *"we are 60% sure"* — and the honest answer
   * to that is to go and find out, not to put the number on the wall. **It stays in the persona
   * file.**
   *
   * **On `AFTER`: removed, as instructed.** `persona_Maya-Founder.md` has it as `none yet` at
   * confidence 0, and he was explicit that a phase with no numbers cannot be shown. **A real loss,
   * and the right call**: the gap was honest but it spent a third of the deck's opening slide saying
   * we had not looked. It belongs in `@P346` as the first hypothesis to test.
   *
   * **And the two numbers are now the slide** — display size, with the sentence that qualifies them
   * wrapped beneath and the source in the mono line under that. Sources are kept because the PO's
   * earlier note on this slide was *"PLZ we cant make up numbers"*, and a vendor-sourced estimate
   * printed bare is indistinguishable from an invented one.
   */
  const figures: Array<{ big: string; then: string; src: string }> = [
    {
      big: '47 hrs',
      then: 'over three weeks to build one investor list — and 88% of it was wrong-fit',
      src: 'SheetVenture, Mar 2026 · Indie Hackers, Apr 2026 · r/startups, Oct 2026',
    },
    {
      big: '95%',
      then: 'of cold emails to investors are never answered. The median seed round takes 142 days — it was 69 in 2021.',
      src: "Founders' Capital · Carta, State of Seed 2026",
    },
  ];
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_pipeline.jpg', alt: 'Abstract: fragmented tools that do not talk to each other', cap: 'the problem' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The problem</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Meet Maya.
          <small>Pre-seed founder, Hong Kong, raising her first institutional round — no fundraising team behind her, so she is the fundraising department.</small>
        </motion.h1>
        <motion.div className="pd-figs" variants={stagger}>
          {figures.map((f) => (
            <div className="pd-fig" key={f.big}>
              <span className="pd-fig-big">{f.big}</span>
              <span className="pd-fig-then">{f.then}</span>
              <span className="pd-fig-src">{f.src}</span>
            </div>
          ))}
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp}>
          The problem is the list and the intro — not the pitch.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ----------------------------------------- 4 the proof it already works */
export function M4Proof({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_traction.jpg', alt: 'One-person companies', cap: 'the proof' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The proof it already works</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The one-person company is not a thesis.
          <small>It is already the best-performing structure we can point at.</small>
        </motion.h1>
        {/* ⚠️ FOUR CARDS BECAME THREE (PO 2026-10-09: *"slide 05 why are 4"*). The fourth was
            *"Sam Altman's standing bet"* — an OPINION sitting beside three checkable facts. A slide
            whose whole subject is *"this is already true"* is weakened by putting a forecast in the
            same row as evidence, because the reader cannot tell which kind of thing she is being
            shown. The three that remain all happened, and each cites its source. */}
        <motion.div className="pd-stats pd-stats-3" variants={stagger}>
          <Kpi v="$401M" l="Medvi — first-year sales" s="one founder · $20K capital · no employees · 16.2% net margin (NYT via PYMNTS, Apr 2026)" />
          <Kpi v="Real" l="Andon Labs — agents run shops" s="retail stores, cafés and procurement running now — not simulations (Gigazine, Sep 2026)" />
          <Kpi v="$8.7M" l="the memory layer is fundable" s="General Intelligence Company raised this on a memory system for one-person companies (PYMNTS, Dec 2025)" />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------ 5 the forgetting problem */
export function M5Forget({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_data.jpg', alt: 'Tools that do not talk', cap: 'the gap' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The problem Raisy solves</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Every tool remembers its own thing. None of them talk.
          <small>So the founder becomes the integration layer — and that is the bottleneck.</small>
        </motion.h1>
        <motion.div className="pd-terms" variants={stagger}>
          <div className="pd-row"><span>CRM</span><span>remembers leads and pipeline stages</span></div>
          <div className="pd-row"><span>Email tool</span><span>remembers templates and send history</span></div>
          <div className="pd-row"><span>Research tool</span><span>remembers investor lists and firmographics</span></div>
          <div className="pd-row"><span>Spreadsheet</span><span>remembers everything, badly</span></div>
          <div className="pd-row"><span>Maya</span><span>remembers everything else</span></div>
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Source: Gartner, 2025 — over 40% of enterprise applications will embed AI agents by the end of
          2026, most of them operating in silos.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------------ 6 what we ship today */
export function M6Ship({ dir }: { dir: number }) {
  /*
   * THE TWO-LAYER JOURNEY (PO 2026-10-09, and his "biggest single build" from the 2026-10-07
   * review). His words, twice:
   *
   *   "The upper layer is what the user does. The lower layer is all we do."
   *   "Take the Maya slide and create a vision here of what the journey of Maya is really
   *    like… and how easy it is, and then we show underneath the hood all the stuff we're
   *    doing — that's the value."
   *
   * So the slide is ONE drawing with two bands, not two lists. YOU do five things, and each is
   * a pill: setup, connect, approve, show up, sign. Under every pill sit the machines that make
   * it that small — and the CONTRAST is the argument. The upper band is nearly empty on purpose:
   * its emptiness is the product.
   *
   * ⚠️ THE FIVE ARE HIS (he lists four and says "five things"): sets up once · connects the
   * application · approves messages · attends meetings to close. The fifth is the signature the
   * meeting produces, because a fundraising journey that stops at "attend" has no outcome, and
   * the deck's whole claim is that we run to the close.
   *
   * THE LOWER BAND IS NOT INVENTED EITHER. Every item is something the product does in the
   * 40-second setup the deck already claims (`M7Traction`: "the 40-second setup"), and the
   * 3,552 scored investors are the number the deck and the model both carry.
   */
  /* FOUR, NOT FIVE (PO 2026-10-09). *"set up once and connect your apps is also done once, ideally I
   * want to combine the first 2"* — and he is right on the count, not just the wording: the deck's
   * own claim is that a URL IS the entity, so setting up and connecting are one act, and listing
   * them apart made the human's side look like it had more steps than it does. **The upper band's
   * shortness is the argument**, so an inflated list undercuts the slide. His two renames land with
   * it: *"Show up" → "Attend Meetings"* and *"Sign" → "Approve Deal"* — both concrete, and both
   * verbs a founder recognises from her own week. */
  /* EXPLICIT PAIRS, not two parallel arrays. They were `you[i]` and `we[i]`, and merging the first
   * two dropped `you` to four while `we` stayed five — which renders four correct cards and silently
   * discards the fifth. A positional pairing cannot survive an edit to either side; this one cannot
   * be edited out of step. */
  const pairs: Array<[string, string, string]> = [
    ['Set up once', 'Reads your URL', 'builds the entity — no forms, no calls'],
    ['Approve the send', '3,552 investors scored', 'researched, ranked, and a draft written from your kernel'],
    ['Attend meetings', 'Schedules and chases', 'books it, and follows up on the clock'],
    ['Approve the deal', 'Tracks and remembers', 'every outcome receipted'],
  ];
  return (
    /* NO SIDE IMAGE, AND THAT IS THE FIX RATHER THAN A PREFERENCE. A two-band diagram is
       FIVE columns wide; inside the ~700px body a side image leaves, each column is ~130px and
       every sub-line wraps to three or four, which made the slide 1.8x the available height and
       drove `Shell`'s auto-fit to its 0.55 floor — the unreadable end of its own clamp. The deck's
       one existing diagram (the market rings) already takes the full width for the same reason. */
    <Shell
      custom={dir}
      className="pd-wide-m"
      /* ⚠️ IT HAD NO BACKGROUND (PO 2026-10-09: *"Slide 04 (one loop) doesnt have a background"*).
         The side image was dropped when the two-band diagram needed the full width, and nothing
         replaced it — so the slide read as a blank page beside twelve that are not. The `bg` slot
         costs no width: the image sits behind the body and the scrim keeps the diagram legible. */
      bg={
        <>
          <img className="pd-img" src="/pitch/s_product.jpg" alt="" />
          <div className="pd-scrim pd-scrim-soft" />
        </>
      }
    >
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>What we ship today</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          One loop, already running.
          <small>You do four things. Everything else is ours.</small>
        </motion.h1>
        <motion.div className="pd-two" variants={fadeUp}>
          <div className="pd-two-rail">
            <span className="pd-two-you-tag">You</span>
            <span className="pd-two-we-tag">Raisy</span>
          </div>
          <div className="pd-two-cols" style={{ gridTemplateColumns: `repeat(${pairs.length}, 1fr)` }}>
            {pairs.map(([y, m, d]) => (
              <div className="pd-two-col" key={y}>
                <div className="pd-two-you">{y}</div>
                <div className="pd-two-link" aria-hidden="true" />
                <div className="pd-two-we">
                  <b>{m}</b>
                  <i>{d}</i>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
        {/* NO CHIPS ROW HERE. It carried "v1.0 shipped 6 September 2026" and "the 40-second
            setup" — both already on the traction slide — and cost ~50px on the tightest diagram in
            the deck. Measured, not guessed: see `D-131`. */}
      </motion.div>
    </Shell>
  );
}

/* -------------------------------------------- 7 the honest traction slide */
export function M7Traction({ dir }: { dir: number }) {
  /*
   * THE HONEST STATE, AS A BOARD RATHER THAN A LIST (PO 2026-10-09: *"it's okay. Nobody reads this
   * text. Proper visualized… this needs to be properly visualized, not just text."*).
   *
   * Six rows of text became six states you can read at a glance, because the slide's whole subject
   * is WHICH OF THEM EXIST. `have` is not decoration: the four we have are solid and the two we do
   * not are dashed outlines, so the honesty is the layout rather than a sentence claiming it.
   *
   * ⚠️ AND THE TWO EMPTY ONES ARE THE POINT. An investor is deciding whether to believe the four;
   * a deck that showed only those four would be selling, and this slide's job is the opposite.
   * "Paying customers · none" is kept in full — it is the strongest honesty in the deck, and the
   * line under it is why it is none.
   */
  const assets: Array<[string, string, string, boolean]> = [
    ['Product', 'v1.0', 'shipped 6 September 2026', true],
    ['Design partner', 'PopMeals', 'Jonathan Weins, CEO · 51–200 staff, $10M+ revenue', true],
    ['Community', 'SuperCharger', 'advanced to the final round', true],
    ['Dogfooding', 'our own raise', 'running on Raisy since 21 September 2026', true],
    ['Waitlist', '4 founders', 'pre-launch, no card required', false],
    ['Paying customers', 'none', 'the free tier opens before the paywall does', false],
  ];
  return (
    /* full width, same reason as the journey — a 3-up board in a half-width column wraps every
       label onto two lines and its two dashed cards stop reading as a pair */
    <Shell custom={dir} className="pd-wide-m">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Traction — the honest version</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          We are pre-revenue.
          <small>Four of these exist. Two do not. The two are on the slide on purpose.</small>
        </motion.h1>
        <motion.div className="pd-assets" variants={stagger}>
          {assets.map(([label, value, note, have]) => (
            <div className={`pd-asset${have ? ' have' : ''}`} key={label}>
              <span className="pd-asset-state">{have ? '●' : '○'}</span>
              <div className="pd-asset-body">
                <span className="pd-asset-label">{label}</span>
                <span className="pd-asset-value">{value}</span>
                <span className="pd-asset-note">{note}</span>
              </div>
            </div>
          ))}
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          The bet: founders will not pay before they try. The free tier is open to five real investor
          emails — no card, no trial clock.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* --------------------------------------------------- 8 the category answer */
export function M8Category({ dir }: { dir: number }) {
  const domains = [
    ['Fundraising', 'Investors', 'Pitch', 'Partner meetings', 'SAFE / terms'],
    ['Sales', 'Buyers', 'Discovery', 'Demo', 'Sales contract'],
    ['Recruiting', 'Candidates', 'Screening', 'Interview', 'Offer'],
    ['Procurement', 'Suppliers', 'RFP', 'Negotiation', 'Supplier contract'],
  ];
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_vision.jpg', alt: 'One system, every domain', cap: 'the category' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The category</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Every B2B process is a sales process.
          <small>Connect → Communicate → Meet → Sign. Same four pillars, different domain, one system.</small>
        </motion.h1>
        <motion.div className="pd-terms" variants={stagger}>
          {domains.map((d) => (
            <div className="pd-row" key={d[0]}>
              <span>{d[0]}</span><span>{d[1]} · {d[2]} · {d[3]} · {d[4]}</span>
            </div>
          ))}
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp}>
          Fundraising is our entry. Not our ceiling. <b>Metal and Platvix end at the round close. We end
          when the company does.</b>
        </motion.p>
        <motion.p className="pd-fine" variants={fadeUp}>
          Sources: metal.so · platvix.com, public materials, 2026.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ---------------------------------------------------------- 9 the value win */
export function M9Value({ dir }: { dir: number }) {
  /* THE VALUE SLIDE, RECHARGED (D-119). It shipped printing `~$1.1M` against `$279/mo` and a
   * closing line that said, on the slide: "Open: the workload basis and the tier basis are
   * reconciled by the PO before this slide ships." A slide that admits it is unresolved cannot be
   * released, and the two numbers could not both be true — the tier is $3,348/yr, not $24K.
   *
   * `~$1.1M` appears NOWHERE in the model. What the model does carry is the answer, computed
   * rather than asserted (FM-0013 D-098, ASSUMPTIONS_AND_BENCHMARKS §B):
   *
   *     one adopted customer is worth $6,319/yr — 10.5x the $600 subscription
   *
   * The PO's instruction was to use KERNEL's numbers, and this is KERNEL's own framing of its own
   * result. The subscription is the floor; the 1% success fee attaches to the customer's own
   * transaction and the services compound on top of it.
   */
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_kernel.jpg', alt: 'One customer, ten times the subscription', cap: 'the value' }}>
      <Kicker>The value</Kicker>
      <motion.h1 className="pd-h1" variants={fadeUp}>
        One customer is worth ten times the subscription.
        <small>We start with the raise. The same customer then adopts sales, hiring and procurement — on one memory.</small>
      </motion.h1>
      <motion.div className="pd-eco" variants={stagger}>
        <Stat n="$600" l="the subscription, per year" s="the floor — one tier, no per-seat maths" />
        <Stat n="$6,319" l="per adopted customer, per year" s="10.5× the subscription: the 1% success fee plus the services it unlocks" />
      </motion.div>
      <motion.p className="pd-fine" variants={fadeUp}>
        Computed by the financial model, not asserted — the 1% attaches to the customer's own
        transaction, so the fee only exists when their outcome does.
      </motion.p>
    </Shell>
  );
}

/* ------------------------------------------------------ 10 the moat and gate */
export function M10Moat({ dir }: { dir: number }) {
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_moat.jpg', alt: 'Every decision trains the next one', cap: 'the moat' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The moat, and the gate</Kicker>
        {/* THE MESSAGE CHANGED (D-101). The PO: "it's not about the data set. We will use the
            data set in slide ten to make our Sales LLM. That message has to change." The slide
            argued that OWNING the labelled dataset is itself the moat; it now says what the
            dataset is FOR. Three rows and one line, not four stat cards and two paragraphs —
            which is also what fixes its formatting. */}
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Every decision trains the next one.
          <small>The dataset is not the moat. What we build from it is — and nothing leaves without a human.</small>
        </motion.h1>
        <motion.div className="pd-terms" variants={stagger}>
          <div className="pd-row"><span>Labelled outcomes</span><span>every email, reply, meeting and signed deal carries what worked</span></div>
          <div className="pd-row"><span>Sales LLM</span><span>trained on our own outcomes — not on the internet</span></div>
          <div className="pd-row"><span>The gate</span><span>a human approves every exit; autonomy is earned above 80% confidence</span></div>
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Corrections are anonymised into patterns, never shared as content. Generic models have read the
          internet; they have never seen ten thousand real outcomes with the KPI attached.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------------------ 12 the model */
export function M12Model({ dir }: { dir: number }) {
  const tiers = [
    ['Free', '$0', '5 reachouts', '—'],
    ['Starter', '$49', '250 / mo', '$7.54'],
    ['Growth', '$129', '750 / mo', '$6.62'],
    ['Scale', '$279', '1,700 / mo', '$6.31'],
  ];
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_matrix.jpg', alt: 'The pricing ladder', cap: 'the model' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Business model — SaaS + Success Fee</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Normal SaaS, plus 1% when the outcome lands.
          <small>The outcome is the transaction that actually happened — not the email that hoped for it.</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          <b>1% success fee</b> — paid when a deal closes, a product sells, a hire is signed.
          Cancel anytime. <b>No equity.</b>
        </motion.p>
        <motion.div className="pd-pricing" variants={stagger}>
          <div className="pd-terms">
            {tiers.map((t) => (
              <div className="pd-row" key={t[0]}>
                <span><b>{t[0]}</b> — {t[1]}/mo</span>
                <span>{t[2]} · est. {t[3]} per meeting</span>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div className="pd-chips" variants={stagger} style={{ marginTop: 16 }}>
          <motion.span className="pd-chip" variants={pop}>$50K MRR by month 12</motion.span>
          <motion.span className="pd-chip" variants={pop}>$1.2M ARR by month 24</motion.span>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* -------------------------------------------------------------- 13 the GTM */
/* ------------------------------------------- 8 why we are different: the machinery */
/* WHY THIS SLIDE EXISTS (D-121). The PO: "I feel we are missing that we have a systematic
 * process layer and with KPI's around it. shouldn't we have a slide showing why we are
 * different from other?"
 *
 * He is right, and the deck proved it by omission: the category slide CLAIMS we are a Company OS
 * and never shows the machine that makes it one. A reader is asked to believe "not a tool"
 * without being shown the layer that differs from a tool.
 *
 * THE CLAIM IS THE ARCHITECTURE, AND IT IS ALREADY LAW. `RAISY_01_MANIFEST.md`: "The four levels
 * are Entity, Process, Bot, Kernel", built top-down and never bottom-up. KPIs are deterministic
 * at every level, and every LLM call is logged with its exact kernel versions. None of that is
 * new copy — it is the system as the manifest describes it, finally on a slide.
 *
 * THE CONTRAST IS THE POINT, AND IT IS NOT A SMEAR. A model (Claude, GPT) is a model: capable,
 * and not a management layer. A workflow tool (Zapier, n8n) makes you design, version, monitor
 * and improve everything yourself. Both are the competitors' own positioning, restated.
 */
export function MDifferent({ dir }: { dir: number }) {
  const levels = [
    ['Entity', 'the company, as the system sees it'],
    ['Process', 'the work it runs — fundraise, sell, hire, buy'],
    ['Bot', 'who does each step, assembled from kernels'],
    ['Kernel', 'what it knows — versioned, and never overwritten'],
  ];
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_kernel.jpg', alt: 'The four-level layer', cap: 'the difference' }}>
      <Kicker>Why we are different</Kicker>
      <motion.h1 className="pd-h1" variants={fadeUp}>
        Everyone else sells a model. We sell the management layer.
        <small>Four levels, top-down: Entity → Process → Bot → Kernel. Not a chat, and not a workflow you maintain yourself.</small>
      </motion.h1>
      <motion.div className="pd-terms" variants={stagger}>
        {levels.map((l, i) => (
          <div className="pd-row" key={l[0]}>
            <span><b>{i + 1} · {l[0]}</b></span>
            <span>{l[1]}</span>
          </div>
        ))}
      </motion.div>
      <motion.div className="pd-stats pd-stats-3" variants={stagger}>
        <Kpi v="Every level" l="carries its own KPIs" s="deterministic — the number does not depend on a prompt" />
        <Kpi v="Every call" l="logged with its kernel version" s="the audit trail is the product, not an add-on" />
        <Kpi v="Earned" l="autonomy, above 80% confidence" s="the gate widens only where the record says it should" />
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------- 13 the field, two axes only */
/* The PDF's slide, named in this file's own header since the deck was built and
 * never actually written (competitor analysis is a step in the sequence; D-097).
 * TWO differentiators, one per axis — where the product ends, and whether anyone
 * can check it. Facts are citable: Metal's price and its missing audit trail come
 * from our own vendor call (meeting_20260910_metal); the scope lines from both
 * vendors' public materials. */
export function MCompetition({ dir }: { dir: number }) {
  const field = [
    ['Metal', 'Fundraising CRM. Ends at the round close. No audit trail. $600 a quarter.'],
    ['Platvix', 'Fundraising engine. Ends at the round close. Basic audit.'],
    ['Raisy', 'Company OS. The memory persists into sales, recruiting, procurement. Approval gate and full audit trail on every exit.'],
  ];
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_autonomy.jpg', alt: 'The field, on two axes', cap: 'the field' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The field</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Two things decide it: where it ends, and who can prove it.
          <small>Both axes are the same thing looked at twice — a system that forgets at the round close cannot show you what it did.</small>
        </motion.h1>
        <motion.div className="pd-terms" variants={stagger}>
          {field.map((f) => (
            <div className="pd-row" key={f[0]}><span>{f[0]}</span><span>{f[1]}</span></div>
          ))}
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp}>
          Metal and Platvix end at the round close. <b>We end when the company does.</b>
        </motion.p>
        <motion.p className="pd-fine" variants={fadeUp}>
          Sources: metal.so · platvix.com, public materials, 2026 · Metal pricing and the audit-trail
          gap from our own vendor call, 2026-09-10.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------------------------- 14 GTM */
export function M13Gtm({ dir }: { dir: number }) {
  /*
   * THE SERVICE ROLLOUT — one counterpart per quarter, all nine (PO 2026-10-09).
   *
   * Two notes from him, and the second one corrected a mistake of mine:
   *
   *   *"for slide 12. make sure that instead of M1, and M13 you show Q4-26 / M13 instead into:
   *    'Q1-26' and so on. some are missing here from counterparty Roster"*
   *
   * ⚠️ **AND "SOME ARE MISSING" WAS EXACTLY RIGHT, BECAUSE THE SLIDE WAS READING THE WRONG BLOCK.**
   * The first roadmap drew `sales_plan.roles` — the HIRES — and got five rungs: M1, M13, M19, M25,
   * M31. **The ladder is not the hires.** It is `revenue_streams`, and its own note states the
   * sequence: *"the ladder now covers ALL NINE roster counterparts, one per quarter… M10, M13, M16,
   * M19, M22, M25, M28, M31, M34 — fee-bearing motions first"*, with `verify:capacity` asserting the
   * cadence. So the slide was showing five of nine, and it was showing the people we hire instead of
   * the services we sell.
   *
   * **I had also accused those months of being invented, and they were not.** See `D-135`: the
   * earlier search covered the hires and not the services, and the hires land near the wedges, which
   * is precisely why the numbers looked plausible enough to accuse. The months and the quarters are
   * now DERIVED in the generator (`ROLLOUT`) so no slide does date arithmetic against a literal.
   *
   * The quarter label is the PO's own ask, and it is the right one: `Q4-26` is a date an investor can
   * hold in her head, and `M10` is not.
   */
  return (
    <Shell custom={dir} className="pd-wide-m">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Go-to-market — the service rollout</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          One service a quarter, on the customers we already have.
          <small>
            Fundraising brings the customer in. Every other lane attaches to her — the same four
            pillars, a different counterpart, on one memory. Nine counterparts, ten motions.
          </small>
        </motion.h1>
        <motion.div className="pd-roll" variants={stagger}>
          {ROLLOUT.map((r) => (
            <div className={`pd-roll-node${r.incumbent ? ' live' : ''}`} key={r.key}>
              <span className="pd-roll-q">{r.quarter}</span>
              <span className="pd-roll-m">M{r.month}</span>
              <span className="pd-roll-dot" aria-hidden="true" />
              <b className="pd-roll-name">{r.label}</b>
              <span className="pd-roll-deliv">{r.deliverable}</span>
              {r.incumbent && <span className="pd-roll-live">live today</span>}
            </div>
          ))}
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp}>
          A fundraising agency charges <b>$5,000–25,000 a month plus a success fee</b>. Our Growth tier
          delivers a comparable execution layer for <b>$129 a month</b>.
        </motion.p>
        <motion.p className="pd-fine" variants={fadeUp}>
          Launch months and quarters read from the financial model — one counterpart per quarter, a
          cadence the model asserts on generation.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------- 15 the round — the ask */
const MEETING_URL = 'https://calendar.app.google/zhnXrGWczfD9bbM98';

/* Colour is presentation, not a business constant, so it lives here while the slices come from
 * the generated model mirror. Four sequential teals for the four working buckets and a NEUTRAL
 * grey for Reserve — it is the one bucket that is not spend. */
const PIE_COLORS = ['#0AB3BF', '#35D0DB', '#6DE6EC', '#A8F0F4', '#5B6E75'];

/* `Lead` used to be written out here to dodge importing the page that imports this module.
 * It lives in ./deckLead now, which both sides import — one definition, not two that merely
 * happen to agree today. */
export function M15Round({
  dir,
  lead,
  onOpenAppendix,
}: {
  dir: number;
  nameRef: React.RefObject<HTMLInputElement | null>;
  lead: Lead;
  onOpenAppendix?: () => void;
}) {
  // THE LABELS ARE THE DECK'S OWN CONTENT; THE NUMBERS ARE THE MODEL'S (D-084).
  // The model's buckets are named generically ("Marketing & Sales"); this slide's agreed
  // wording is "GTM & design partners", and a content change belongs in the deck source
  // (D-075), not smuggled in by a regeneration. So the pairing is POSITIONAL, and the order
  // is the contract: five buckets, same order on both sides. If the model ever adds a bucket,
  // the length check fails loudly here rather than silently mislabelling a slice.
  const ALLOC_LABELS = ['Engineering', 'GTM & design partners', 'Compliance & security', 'Team & ops', 'Reserve'];
  if (ALLOC_LABELS.length !== PROCEEDS.length) {
    throw new Error(
      `slidesMaster M15Round: the deck names ${ALLOC_LABELS.length} use-of-proceeds buckets, the model has ${PROCEEDS.length}. Reconcile them in the deck source and in gen_market_by_year.ts together.`,
    );
  }
  /* which slice the pointer is on — the legend and the ring highlight together */
  const [slice, setSlice] = useState<string | null>(null);
  const alloc = PROCEEDS.map((p, i) => [ALLOC_LABELS[i] ?? '', `${Math.round(p.pct * 100)}%`, `$${Math.round(p.amount / 1000)}K`]);
  const usdK = (n: number) => `$${Math.round(n / 1000)}K`;
  /* THE NUMBER MUST SURVIVE ITS OWN FORMATTING. `usdM()` renders $346,836 as "$0.3M" — a
   * precision loss that turns the strongest figure on the slide into the weakest-looking one.
   * Returns are stated in full. */
  const usdFull = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_team.jpg', alt: 'The round and what it buys', cap: 'the ask' }}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Join the Raisy round</Kicker>
        {/* THE DEAL FROM THE INVESTOR'S SIDE (D-115). The PO: "we just show a few GREAT numbers.
            not valuation." So the slide stops printing the cap and prints what a ticket RETURNS —
            all three read from the model through the generated mirror, never typed:

              ROUND_MOIC / ROUND_IRR / MIN_TICKET_EXIT_PROCEEDS.

            The valuation is still in the model and still available on request; it is simply not
            the thing this slide says. See D-115 for what that costs (the angel persona's W2) and
            for the model's own open price question, which this no longer puts on the page. */}
        <motion.h1 className="pd-h1" variants={fadeUp}>
          {usdK(PROCEEDS_ROUND)} pre-seed
          {/* SHORT, BECAUSE THE NUMBER IS ALREADY BELOW. This line used to repeat $346,836, which
              the third figure carries two lines down — the same number twice on one slide, and
              the repeat was what wrapped the line to two rows and cost the slide ~40px it did not
              have. The terms belong here; the returns belong in the figures. */}
          <small>SAFE · {usdK(MIN_TICKET_SIZE)} minimum ticket · the round is open now</small>
        </motion.h1>
        {/* THREE COLUMNS, ONE ROW. At the shared two-column grid these three cards wrapped into
            two rows and pushed the slide into an auto-fit shrink (measured 93% at 864px tall —
            content needed ~916px). One row gives the height back. `pd-stats-3` is local to this
            slide; the other slides' grids are untouched. */}
        <motion.div className="pd-stats pd-stats-3" variants={stagger}>
          <Kpi v={`${ROUND_MOIC.toFixed(1)}×`} l="return on the round" s="the model's own multiple" />
          <Kpi v={`${(ROUND_IRR * 100).toFixed(1)}%`} l="annual return on the round" s={`across ${EXIT_HORIZON_YEARS} years`} />
          <Kpi v={usdFull(MIN_TICKET_EXIT_PROCEEDS)} l="back from one $50K ticket" s="at the modelled exit" />
        </motion.div>
        {/* USE OF PROCEEDS AS A DONUT (D-117). The PO: "this needs to be a pie chart." The rows
            became five lines of text stacked down the slide; the split is a proportion, and a
            proportion wants a shape.

            Ported from `slidesV2.tsx`'s DonutV2, which is the deck's own hand-built SVG donut —
            not drawn from the blog's 61 components, so it keeps the deck idiom. Slices still come
            from the generated ./deckFinancials.ts, so the chart cannot drift from the model.

            FIVE COLOURS, NOT FOUR. DonutV2's palette has four entries for five buckets, so
            `i % 4` gave Reserve the same colour as Engineering. Reserve is also the one bucket
            that is not spend, so it takes a NEUTRAL grey rather than another teal. */}
        <motion.div className="pd-proceeds-m" variants={fadeUp} onMouseLeave={() => setSlice(null)}>
          <svg className="pd-donut-m" viewBox="0 0 200 200" role="img" aria-label="Use of proceeds">
            <title>Use of proceeds: {alloc.map((a) => `${a[1]} ${a[0]}`).join(', ')}</title>
            <g transform="rotate(-90 100 100)">
            {(() => {
              const R = 72, SW = 27, CIRC = 2 * Math.PI * R, GAP = 3.2;
              let pos = 0;
              return PROCEEDS.map((p, i) => {
                const seg = Math.max(p.pct * CIRC - GAP, 2);
                const offset = CIRC - pos;
                pos += p.pct * CIRC;
                const on = slice === ALLOC_LABELS[i];
                return (
                  <circle
                    key={ALLOC_LABELS[i]}
                    cx={100} cy={100} r={R} fill="none"
                    stroke={PIE_COLORS[i]}
                    strokeWidth={SW}
                    strokeDasharray={`${seg} ${CIRC - seg}`}
                    strokeDashoffset={offset}
                    opacity={slice === null || on ? 1 : 0.32}
                    style={{ transition: 'opacity .3s' }}
                    onMouseEnter={() => setSlice(ALLOC_LABELS[i] ?? null)}
                  />
                );
              });
            })()}
            </g>
            <text x={100} y={96} className="pd-donut-top-m">{usdK(PROCEEDS_ROUND)}</text>
            <text x={100} y={114} className="pd-donut-sub-m">allocated</text>
          </svg>
          <ul className="pd-legend-m">
            {alloc.map((a, i) => (
              <li
                key={a[0]}
                className={slice === a[0] ? 'on' : ''}
                onMouseEnter={() => setSlice(a[0])}
              >
                <span className="sw" style={{ background: PIE_COLORS[i] }} />
                <span className="lb">{a[0]}</span>
                <span className="pc">{a[1]}</span>
                <span className="am">{a[2]}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        {/* THE BUSINESS IN FOUR NUMBERS, ON THE SLIDE THAT ASKS (PO 2026-10-09). The appendix
            carries the full set; a reader who will never open an appendix should still be able to
            see that this is a business and not a pitch. Four cards, because the slide already
            states the deal above them — MOIC, IRR and the exit proceeds — so repeating those would
            have been eight numbers saying four things.

            THE BUTTON OPENS THE APPENDIX rather than requesting anything: "Request Financial Model"
            is a commitment, and the appendix is one click. Two levels of ask on one slide, in the
            order a stranger meets them. */}
        <motion.div className="pd-mini-m" variants={fadeUp}>
          <div className="pd-mini-m-cards">
            {KPI_HEADLINE.map(([id, label, note]) => {
              const k = MODEL_KPIS[id];
              return (
                <div className="pd-mini-m-card" key={id}>
                  <b>{formatKpi(k.unit, k.value)}</b>
                  <span>{label}</span>
                  <i>{note}</i>
                </div>
              );
            })}
          </div>
          {onOpenAppendix && (
            <button className="pd-mini-m-btn" onClick={onOpenAppendix} title="Open the appendix">
              The model<p>appendix A4</p>
            </button>
          )}
        </motion.div>
        <motion.p className="pd-sub" variants={fadeUp}>
          {/* THE WINDOW (D-097). A time window plus validation is what makes waiting costlier than
              investing; a deck with no date asks the reader to move whenever they like. The date is
              the model's own public first close (`round_schedule.tranches`), not a remembered one —
              the earlier "opens 1 October" went stale the moment it passed. */}
          <b>First close 15 November 2026.</b> Milestone: <b>$1.2M ARR by month 24</b>.
        </motion.p>
        {/* THE ONE ASK (D-100). This slide used to carry a bare name/email input, which asked
            the reader for the same two fields the entrance gate had already taken — asking twice
            is the tell that the first ask was for us rather than for them. The gate's identity is
            carried in, shown back, and the call to action is the next step the deck is FOR: a
            scheduled meeting. */}
        <div className="pd-ask-ctas">
          <a className="pd-cta-main" href={MEETING_URL} target="_blank" rel="noopener noreferrer">
            Schedule a meeting
            <span>
              {lead?.name ? `30 minutes, straight in the diary — you're in as ${lead.name}` : '30 minutes, straight in the diary'}
            </span>
          </a>
        </div>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------------------- 16 the close */
export function M16Vision({ dir }: { dir: number }) {
  /*
   * ⚠️ THIS REPLACES A GRAPH THE PO NEVER ASKED FOR. He said *"multiple raisy's connecting somehow
   * as an economy"* and I built a hub-and-spoke: one Raisy, four lanes, drawn wires. He came back:
   * *"why did you change the graph. i never asked for that. If you want to go for this then you must
   * show the similarity between those. then the components these evolvement are sharing."* Then he
   * named the thing he actually wanted:
   *
   *   *"you must show the input/process/output per entity / per process and per bot. each have a
   *    signature, on a different level like 'yellow pages' in germany, we can match if you buy and
   *    sell. just give a good example!"*
   *
   * **And the corpus already specifies it exactly — this is `MR14`, not a new idea.** Its own words:
   * *"Actors → Entities. Goods/Services → OFFERING kernel + REQUIREMENT kernel. **Market →
   * matchmaking: OFFERING ↔ REQUIREMENT compatibility.**"* And the directory he means is **UNSPSC**:
   * *"United Nations Standard Products and Services Code… Segment (2) → Family (4) → Class (6) →
   * Commodity (8 digits). **Free. Global. Machine-matchable.**"* — a shared code space is what lets
   * two parties who have never met find each other, which is precisely a Yellow Pages.
   *
   * **THE SIMILARITY AND THE SHARED COMPONENT HE ASKED FOR ARE THE SAME THING: every level runs
   * INFLOW → OPERATIONS → OUTFLOW.** `R011` tags each process step INFLOW / OPERATIONS / OUTFLOW /
   * COMPLIANCE, and `R013` turns **every OUTFLOW item into an OFFERING** and **every INFLOW item into
   * a REQUIREMENT**, each with its own code. So the pattern is identical at Entity, Process and Bot
   * level, and the shared component is the signature.
   *
   * ⚠️ AND NO CODE BELOW IS INVENTED. `MR14` is explicit that *"R013 does NOT guess codes from
   * memory — LLMs cannot reliably recall 55,000+ codes"*, so the only code shown is the example the
   * manifest itself carries (`81111507` = Software as a service), and the rest name the code's
   * SOURCE rather than a number I cannot verify.
   */
  const levels: Array<{ n: string; name: string; step: string; input: string; process: string; output: string; sig: string }> = [
    {
      n: 'L1',
      name: 'Entity',
      step: 'What the company is',
      input: 'capital, people, tooling',
      process: 'run the company',
      output: 'equity · a product',
      sig: 'GICS sector · UNSPSC of what it sells',
    },
    {
      n: 'L2',
      name: 'Process',
      step: 'One piece of the work',
      input: 'a target list of 500 investors',
      process: 'fundraising — outreach, follow-up',
      output: 'a qualified meeting held',
      sig: 'every INFLOW and OUTFLOW item, coded',
    },
    {
      n: 'L3',
      name: 'Bot',
      step: 'Who runs the step',
      input: 'your URL',
      process: 'research, rank, draft',
      output: '3,552 scored investors',
      sig: 'the kernel version it ran',
    },
  ];
  return (
    <Shell custom={dir} className="pd-wide-m">
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The long-term vision</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The workplace of the future is not a place. It is an economy.
          <small>
            Every level of the system — the entity, each process, every bot — takes an input, does the
            work, and produces an output. Code what it produces the way the whole world codes it, and
            a buyer you have never met can find you.
          </small>
        </motion.h1>

        <motion.div className="pd-sig-head" variants={fadeUp}>
          <span className="pd-sig-c1">Level</span>
          <span>Input &mdash; what it needs</span>
          <span>Process &mdash; what it does</span>
          <span>Output &mdash; what it produces</span>
          <span className="pd-sig-c5">Signature</span>
        </motion.div>
        <motion.div className="pd-sigs" variants={stagger}>
          {levels.map((l) => (
            <div className="pd-sig" key={l.n}>
              <span className="pd-sig-c1">
                <b>{l.n}</b>
                <i>{l.name}</i>
                <em>{l.step}</em>
              </span>
              <span className="pd-sig-cell in">{l.input}</span>
              <span className="pd-sig-cell pr">{l.process}</span>
              <span className="pd-sig-cell out">{l.output}</span>
              <span className="pd-sig-c5">{l.sig}</span>
            </div>
          ))}
        </motion.div>

        <motion.div className="pd-match" variants={fadeUp}>
          <div className="pd-match-side need">
            <span className="pd-match-tag">Her OUTFLOW becomes a code</span>
            <b>&ldquo;a qualified meeting held&rdquo;</b>
            <i>she now SELLS it, priced</i>
          </div>
          <div className="pd-match-mid">
            <span className="pd-match-eq">=</span>
            <span className="pd-match-std">UNSPSC</span>
            <span className="pd-match-note">free · global · machine-matchable</span>
          </div>
          <div className="pd-match-side give">
            <span className="pd-match-tag">His INFLOW carries the same code</span>
            <b>&ldquo;deal flow&rdquo;</b>
            <i>he now BUYS it, without searching</i>
          </div>
        </motion.div>
        <motion.p className="pd-fine" variants={fadeUp}>
          Neither party looked for the other. That is the whole of it: a shared code space like a phone
          book, except the entries are what entities PRODUCE and CONSUME — so the match is a lookup, and
          it works at every level at once. <b>81111507 · software as a service</b> is one entry in it.
        </motion.p>

        <motion.p className="pd-sub" variants={fadeUp}>
          <b>Can one system that never forgets run an entire company?</b> Fundraising is the first
          proof. The economy is what happens when two of them can trade without an introduction.
        </motion.p>
        <motion.p className="pd-fine" variants={fadeUp}>
          v2026.10.05 · Playano Education Ltd · Hong Kong
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/** The master's registry: 14 slides, then the ask, the close and the legal page. */
export const DECK_SLIDES_MASTER: Array<(p: { dir: number }) => ReactElement> = [
  M1Cover,
  /* ⚠️ ORDER SWAPPED (PO 2026-10-09): *"why are we starting with the solution? WE START WITH MAYA.
     switch slide 2 and slide 3."* He is right on the story: the hook is the ANSWER, and a deck that
     states the answer before the problem asks the reader to accept a solution to something she has
     not been shown. The component names stay — they are the deck's own vocabulary for those slides —
     and only their POSITIONS moved. `speakerScript.ts` is indexed by position, so its notes moved
     with them. */
  M3Maya,        // the problem FIRST: who suffers (persona's "personal pain first")
  M2Hook,        // then the shift — now the promise rather than the opening
  M6Ship,        // the product — "demo front and centre"
  M4Proof,       // the proof the model already works
  M7Traction,    // honest built-vs-planned
  M8Category,    // the category answer
  MDifferent,    // why we are different — the machinery, not the claim (D-121)
  M9Value,       // the value win — the persona's "visceral win"
  S9MarketV2,    // the market (FM-0013 mirror)
  M12Model,      // the model
  M13Gtm,        // GTM
  MTeam,         // the team — rebuilt for height (D-118)
];

/* ---------------------------------------------------- the appendix, announced */
/*
 * A DIVIDER, SO THE READER KNOWS WHERE THE PITCH ENDED (PO, 2026-10-09). Without it the deck
 * ran straight from the legal page into material that looks exactly like a slide — so the reader
 * could not tell whether the pitch was still going. An appendix that does not announce itself
 * reads as a deck that lost its ending.
 *
 * It carries NO NUMBER and no note: it is a door, not a page. The counter says "Appendix" over
 * the divider and "A1 / A4" after it, and the top-right badge changes colour here so the state
 * is legible even when the reader has scrolled past this slide.
 */
export function MAppendixDivider({ dir }: { dir: number }) {
  return (
    <Shell
      custom={dir}
      className="pd-appx"
      side={{ src: '/pitch/s_abstract_data.jpg', alt: 'Appendix', cap: 'appendix' }}
    >
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Appendix</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The deck ends here. The questions do not.
          <small>
            Four pages: why the problem happens, what defends it, who else is in the field, and the
            financial model every number in the pitch came from.
          </small>
        </motion.h1>
        <motion.div className="pd-terms" variants={stagger}>
          <div className="pd-row"><span>A1</span><span>Why the problem keeps happening</span></div>
          <div className="pd-row"><span>A2</span><span>What defends it — the gate, and the ledger</span></div>
          <div className="pd-row"><span>A3</span><span>The field, on two axes</span></div>
          <div className="pd-row"><span>A4</span><span>The financial model, and how to request it</span></div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ------------------------------------------- A4 the model, and how to ask for it */
/*
 * THE MODEL, ON THE PAGE (PO, 2026-10-05). He asked where the FM-0013 dashboard's KPI cards were
 * in the deck. The honest answer was: the investor's return is on the ask slide and nothing else
 * was anywhere — so the deck showed a market, a price ladder and a multiple, and left the
 * operating result the multiple is computed FROM entirely unstated.
 *
 * Every value is `MODEL_KPIS`, generated from the model (D-084), and the UNIT travels with the
 * value so a slide cannot pair a number with the wrong format. `npv` is deliberately absent: it
 * is an internal hurdle test rather than a headline, and it is negative at the current minimum
 * ticket, so printing it alone would invite the one question the slide cannot answer.
 *
 * IT IS AN APPENDIX SLIDE ON PURPOSE. The main line is a 120-second story and a wall of eight
 * KPIs is not a story beat — but the appendix IS printed, so an investor reading the PDF and
 * asking "what does the model actually say" gets the numbers without a call. Promoting it into
 * the main line is a one-line move if the PO decides the deck should say this out loud.
 */
export function MModelAppendix({ dir }: { dir: number }) {
  /* The gate captured the lead into sessionStorage; this slide reads it rather than taking it as
   * a prop, because the appendix registry is typed `({dir})` for every slide and widening that
   * type for the deck's last page would change the deck's structure to serve its footer. When
   * there is no lead (a PDF export, a deep link) the button asks for an email instead. */
  const [lead] = useState<Lead | null>(() => readLead());
  const [typed, setTyped] = useState('');
  const [asking, setAsking] = useState(false);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');

  /* ⚠️ THE LEAD IS RE-READ ON CLICK, NOT ONLY AT MOUNT — and the field is revealed rather than
   * rendered up front. The deck renders a hidden print copy of every slide, and that copy mounts
   * BEFORE the gate has run, so an appendix that decides its own shape from `readLead()` alone
   * mounts with no lead and keeps it: the PDF was printing an empty email box and a disabled
   * button for a reader who had already given us both. */
  const request = async () => {
    const who = lead ?? readLead();
    if (!who && !typed.trim()) {
      setAsking(true);
      return;
    }
    setState('sending');
    setState((await postLead(who ?? { name: '', email: typed.trim() }, 'PITCHDECK-MODEL')) ? 'sent' : 'failed');
  };

  return (
    <Shell
      custom={dir}
      className="pd-s13"
      side={{ src: '/pitch/s_abstract_kernel.jpg', alt: 'The financial model behind the deck', cap: 'the model' }}
    >
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>The model behind these numbers</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Every number here is an output, not an opinion.
          <small>
            One workbook, {EXIT_HORIZON_YEARS} years, priced on reachouts sent and a 1% success fee. Ask for it
            and we will send it whole — the assumptions, the drivers and the sensitivities.
          </small>
        </motion.h1>

        <motion.div className="pd-stats pd-kpi-m" variants={stagger}>
          {KPI_CARDS.map(([id, label, note]) => {
            const k = MODEL_KPIS[id];
            return <Stat key={id} n={formatKpi(k.unit, k.value)} l={label} s={note} />;
          })}
        </motion.div>

        <motion.p className="pd-fine" variants={fadeUp}>
          FM-0013, the single case — there is no bull case and no bear case behind it. Investor return
          is computed on the round as priced: {formatKpi(MODEL_KPIS.exitMultiple.unit, MODEL_KPIS.exitMultiple.value)}{' '}
          exit multiple on year-5 ARR, {formatKpi(MODEL_KPIS.breakevenMonth.unit, MODEL_KPIS.breakevenMonth.value)} to
          EBITDA breakeven.
        </motion.p>

        <motion.div className="pd-request-m" variants={fadeUp}>
          {state === 'sent' ? (
            <p className="pd-request-m-done">
              <b>Sent.</b> The model is on its way to {lead?.email ?? typed}.
            </p>
          ) : state === 'failed' ? (
            <p className="pd-request-m-done">
              We could not log that just now — write to us and we will send it.{' '}
              <a href={MEETING_URL}>Book 20 minutes</a> instead.
            </p>
          ) : (
            <>
              {asking && !lead && (
                <input
                  className="pd-request-m-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@fund.com"
                  aria-label="Your email"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && request()}
                />
              )}
              <button className="pd-cta-main pd-request-m-btn" onClick={request} disabled={state === 'sending'}>
                Request Financial Model
                <span>the workbook, its assumptions and its sensitivities</span>
              </button>
              {/* A printed page cannot be clicked, and the PDF is the artefact that travels
                  without us — so it carries the address the button would have used. */}
              <p className="pd-print-only pd-request-m-print">
                Request Financial Model, or ask us anything: david@raisy.team
              </p>
            </>
          )}
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/*
 * THE APPENDIX (D-106). Slides that answer the questions an investor asks when we are NOT in the
 * room — diagnosis depth, defensibility, the competitive field. They were in the main line, which
 * is how the pitch reached 18 slides; the co-founder's v11 keeps exactly this material in an
 * appendix and the standard sequence calls competitor analysis a *supporting* step, not a story
 * beat. The main line stays a story; the depth is one slide further on, and in the PDF.
 *
 * NOT SPOKEN: these carry no speaker note, by construction — `notes` is indexed by the pitch's
 * own numbering, so an appendix slide finds nothing and renders no pill. An appendix is read.
 */
export const APPENDIX_MASTER: Array<(p: { dir: number }) => ReactElement> = [
  M5Forget,      // A1 — why the problem happens (the founder is the integration layer)
  M10Moat,       // A2 — defensibility, and the human gate
  MCompetition,  // A3 — the field, on two axes
  MModelAppendix, // A4 — the model's own KPIs, and the request button (the sheet's second code)
];

export const PRELOAD_MASTER = [
  '/pitch/artsy/c.jpg',
  '/pitch/s_abstract_network.jpg',
  '/pitch/s_abstract_pipeline.jpg',
  '/pitch/s_traction.jpg',
  '/pitch/s_abstract_data.jpg',
  '/pitch/s_product.jpg',
  '/pitch/s_abstract_team.jpg',
  '/pitch/s_vision.jpg',
  '/pitch/s_abstract_kernel.jpg',
  '/pitch/s_moat.jpg',
  '/pitch/s_abstract_matrix.jpg',
  '/pitch/s_autonomy.jpg',
  '/pitch/s_gtm.jpg',
  '/pitch/david.jpg',
  '/pitch/simon.jpg',
  '/pitch/raisy_white.png',
];

/* ------------------------------------------------------------ 12 the two founders */
/* THE TEAM SLIDE, REBUILT (D-118). It was deck C's `S12TeamV2`, and it measured **76%** — the
 * worst slide in the deck by a wide margin, and the one the PO named: "heavy padding at the very
 * top and the bottom part is cut off." Both symptoms are the same fault: the content ran ~24%
 * past the viewport, so auto-fit scaled the whole body down, which shrinks the padding at the top
 * and pushes the advisors past the bottom edge.
 *
 * THE CAUSE WAS THE PORTRAITS. `.pd-tcard img` is `width: 100%` with `aspect-ratio: 1/0.9`, so at
 * ~483px per column each photo was ~435px tall and the two cards alone ate half the slide.
 *
 * FIX: cap the portrait height and let `object-fit: cover` crop from the top, so the face stays
 * visible. 435px -> 168px. A master-local component rather than an edit to `S12TeamV2`'s shared
 * classes, because deck C is stamped and must not change — the same rule that produced `-m`
 * namespacing on the donut (`D-117`).
 */
export function MTeam({ dir }: { dir: number }) {
  const founders = [
    {
      img: '/pitch/david.jpg',
      name: 'David Demmer',
      role: 'Co-Founder · CEO & CTO',
      bio: 'Left his CEO position at a unicorn to focus on Playano. 1 incubation and 3 acceleration programs. Shipped 9 products across the Playano suite. Awarded hedge-fund manager, over $4B AUM multi-asset under SFC license. Ex-CIO/Co-Founder CryptAM (exited).',
    },
    {
      img: '/pitch/simon.jpg',
      name: 'Simon Stegelmeier',
      role: 'Co-Founder · CMO & Analytics',
      bio: '10+ years building enterprise-grade data workflows (Salesforce, Tableau, Data Cloud) for Vaillant, LEGO, ESA and Accenture. He built the governance layer — kernel, memory, audit trail — that makes Raisy enterprise-ready.',
    },
  ];
  return (
    <Shell custom={dir} side={{ src: '/pitch/s_abstract_team.jpg', alt: 'The founders', cap: 'the team' }}>
      <Kicker>The team</Kicker>
      <motion.h1 className="pd-h1" variants={fadeUp}>
        Why we are the right team for this.
        <small>9 shipped products · 1 exit · 4 accelerators — and we are raising on Raisy ourselves.</small>
      </motion.h1>
      <motion.div className="pd-team-m" variants={stagger}>
        {founders.map((f) => (
          <motion.div className="pd-tcard-m" variants={fadeUp} key={f.name}>
            <img src={f.img} alt={f.name} />
            <div className="tinfo-m">
              <h3>{f.name}</h3>
              <div className="role">{f.role}</div>
              <p>{f.bio}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
      <motion.div className="pd-advisors-m" variants={fadeUp}>
        <div className="lbl">Advisors</div>
        <div className="row">
          {ADVISORS.map((a) => (
            <div className="adv" key={a.n}>
              <img src={a.img} alt={a.n} />
              <div>
                <div className="n">{a.n}</div>
                <div className="r">{a.r}</div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </Shell>
  );
}
